import { useState, useEffect } from "react";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Settings, Bell, Globe, Save, Loader2, Building2, Upload } from "lucide-react";

export default function SystemConfigPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/config/platform");
        setConfig(data);
      } catch {
        toast.error("Failed to load configuration");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/config/platform", config);
      setConfig(data);
      toast.success("Configuration saved");
    } catch {
      toast.error("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/config/admin-logo", fd);
      setConfig(prev => ({ ...prev, admin_logo: data.admin_logo }));
      toast.success("Admin logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload logo");
    } finally {
      setLogoUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in max-w-3xl" data-testid="system-config-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="config-title">System Configuration</h1>
          <p className="text-muted-foreground text-sm mt-1">Platform settings, branding, and notifications</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-1.5" data-testid="save-config-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* General Settings */}
      <Card className="mb-6" data-testid="general-settings-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Globe className="w-4 h-4 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base font-heading">General Settings</CardTitle>
              <CardDescription className="text-xs">Platform identity and defaults</CardDescription>
            </div>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-5 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Platform Name</Label>
              <Input
                value={config?.platform_name || ""}
                onChange={(e) => setConfig((p) => ({ ...p, platform_name: e.target.value }))}
                data-testid="config-platform-name"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Support Email</Label>
              <Input
                type="email"
                value={config?.support_email || ""}
                onChange={(e) => setConfig((p) => ({ ...p, support_email: e.target.value }))}
                placeholder="support@yourfund.com"
                data-testid="config-support-email"
              />
            </div>
          </div>
          <div className="w-1/2">
            <Label className="text-xs text-muted-foreground">Default Currency</Label>
            <Select value={config?.default_currency || "USD"} onValueChange={(v) => setConfig((p) => ({ ...p, default_currency: v }))}>
              <SelectTrigger data-testid="config-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD - US Dollar</SelectItem>
                <SelectItem value="EUR">EUR - Euro</SelectItem>
                <SelectItem value="GBP">GBP - British Pound</SelectItem>
                <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Admin / Fund Administrator Settings */}
      <Card className="mb-6" data-testid="admin-settings-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-base font-heading">Fund Administrator</CardTitle>
              <CardDescription className="text-xs">Administrator branding for investor statements</CardDescription>
            </div>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-5 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Admin Name</Label>
              <Input
                value={config?.admin_name || ""}
                onChange={(e) => setConfig((p) => ({ ...p, admin_name: e.target.value }))}
                placeholder="e.g. Fundtec Services LLP"
                data-testid="config-admin-name"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Admin Website</Label>
              <Input
                value={config?.admin_website || ""}
                onChange={(e) => setConfig((p) => ({ ...p, admin_website: e.target.value }))}
                placeholder="e.g. www.fundtec.in"
                data-testid="config-admin-website"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Admin Logo</Label>
            <div className="flex items-center gap-4 mt-2">
              {config?.admin_logo && (
                <div className="w-16 h-16 rounded-lg border border-border bg-secondary/30 flex items-center justify-center overflow-hidden">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/config/admin-logo`} alt="Admin logo" className="max-w-full max-h-full object-contain" />
                </div>
              )}
              <div>
                <Button
                  variant="secondary" size="sm" className="border border-border gap-1.5"
                  onClick={() => document.getElementById("admin-logo-upload").click()}
                  disabled={logoUploading}
                  data-testid="upload-admin-logo-btn"
                >
                  <Upload className="w-3.5 h-3.5" />
                  {logoUploading ? "Uploading..." : config?.admin_logo ? "Change Logo" : "Upload Logo"}
                </Button>
                <input id="admin-logo-upload" type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden" onChange={handleLogoUpload} />
                <p className="text-xs text-muted-foreground mt-1">Appears on investor statements</p>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Admin Disclaimer</Label>
            <Textarea
              value={config?.admin_disclaimer || ""}
              onChange={(e) => setConfig((p) => ({ ...p, admin_disclaimer: e.target.value }))}
              className="min-h-[100px] mt-1"
              placeholder="Disclaimer text for investor statements..."
              data-testid="config-admin-disclaimer"
            />
            <p className="text-xs text-muted-foreground mt-1">This disclaimer appears at the bottom of all investor statements</p>
          </div>
        </CardContent>
      </Card>

      {/* Outgoing Emails */}
      <Card className="mb-6" data-testid="outgoing-emails-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
              <Bell className="w-4 h-4 text-violet-400" />
            </div>
            <div>
              <CardTitle className="text-base font-heading">Outgoing Emails</CardTitle>
              <CardDescription className="text-xs">Enable or disable outgoing email notifications globally</CardDescription>
            </div>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-5">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium">Activate Outgoing Emails</p>
              <p className="text-xs text-muted-foreground">When enabled, the platform will send email notifications to investors and fund managers</p>
            </div>
            <Switch
              checked={config?.outgoing_emails_enabled ?? false}
              onCheckedChange={(v) => setConfig((p) => ({ ...p, outgoing_emails_enabled: v }))}
              data-testid="config-outgoing-emails-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card data-testid="notification-settings-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Bell className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-base font-heading">Notification Preferences</CardTitle>
              <CardDescription className="text-xs">Control which events trigger notifications</CardDescription>
            </div>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-5 space-y-4">
          {[
            { key: "notify_new_investor", label: "New Investor Onboarded", desc: "Notify when a new investor is added to any fund" },
            { key: "notify_document_upload", label: "Document Uploaded", desc: "Notify when documents are uploaded or submitted" },
            { key: "notify_transaction", label: "Transaction Recorded", desc: "Notify when capital or performance transactions are entered" },
            { key: "notify_kyc_change", label: "KYC Status Change", desc: "Notify when an investor's KYC status is updated" },
            { key: "notify_onboarding_complete", label: "Onboarding Completed", desc: "Notify investor when their onboarding is marked as complete" },
            { key: "notify_report_released", label: "Report Released", desc: "Notify investor when a report for any reporting period is released" },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
              <Switch
                checked={config?.[item.key] ?? true}
                onCheckedChange={(v) => setConfig((p) => ({ ...p, [item.key]: v }))}
                data-testid={`config-${item.key}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
